/**
 * StarTrack 埋点公共模块：封装 SDK 脚本加载、实例化、初始化、公共属性注入与事件上报。
 * 不包含心跳、outer-track 等业务逻辑，由各 package 在外层组合。
 */

import { isDebugMode } from '../helper/debug'
import sharedConfig from '../config'
import type { TrackClientOptions, TrackSDKSettings } from './types'

export type { TrackClientOptions, TrackSDKSettings }

// —— 全局 SDK 检测 ——

/** 判断 window.StarTrack 是否已存在 */
export const hasStarTrackSDK = (): boolean => !!(window as any).StarTrack

// —— StarTrackClient ——

const DEFAULT_SETTINGS: TrackSDKSettings = { eventLimit: 1 }

/** 内置公共属性（可被业务覆盖） */
const BUILTIN_COMMON_PROPERTIES = {
  // 1-web；2-Android；3-iOS。本模块固定为 web
  platform_type: 1,
} as const

export class StarTrackClient {
  private options: TrackClientOptions
  private sdkPromise: Promise<void> | null = null
  private initingPromise: Promise<void> | null = null
  private inited = false
  private client: any = null
  private commonProperties: Record<string, any> = { ...BUILTIN_COMMON_PROPERTIES }
  private initStamp = 0

  constructor(options?: TrackClientOptions) {
    this.options = options ?? {}
  }

  // —— SDK 检测 ——

  static hasSDK(): boolean {
    return hasStarTrackSDK()
  }

  // —— SDK 脚本加载 ——

  /**
   * 确保 window.StarTrack 可用。
   * 已就绪 → 立即 resolve；页面已有 script 标签 → 等 onload；都没有 → 动态注入脚本。
   * 多次调用幂等，返回同一个 Promise。
   */
  ensureSDK(): Promise<void> {
    if (this.sdkPromise) return this.sdkPromise

    if (hasStarTrackSDK()) {
      this.sdkPromise = Promise.resolve()
      return this.sdkPromise
    }

    this.sdkPromise = new Promise<void>((resolve, reject) => {
      const src = sharedConfig.trackSDKSrc
      const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null
      if (existing) {
        if (hasStarTrackSDK()) {
          resolve()
          return
        }
        existing.addEventListener('load', () => resolve())
        existing.addEventListener('error', () => reject(new Error('[StarTrackClient] SDK script failed to load')))
        return
      }

      const script = document.createElement('script')
      script.src = src
      script.defer = true
      script.addEventListener('load', () => resolve())
      script.addEventListener('error', () => reject(new Error('[StarTrackClient] SDK script failed to load')))
      document.head.appendChild(script)
    })

    return this.sdkPromise
  }

  // —— 实例化 ——

  /** 从 window.StarTrack 创建独立实例（工厂或构造器） */
  private createInstance(): any {
    const star = (window as any).StarTrack
    if (!star) throw new Error('[StarTrackClient] StarTrack SDK not available')
    return star.createInstance ? star.createInstance() : new star.__proto__.constructor()
  }

  // —— 初始化 ——

  /**
   * 初始化 SDK 实例。幂等，重复调用返回同一个 Promise。
   * 服务端配置从 sharedConfig 读取，调用方仅可传 devMode 等运行时开关。
   */
  init(config?: TrackSDKSettings): Promise<void> {
    if (this.inited) return Promise.resolve()
    if (this.initingPromise) return this.initingPromise

    this.client = this.createInstance()

    const initParams = {
      baseUrl: sharedConfig.trackBaseUrl,
      secret_id: sharedConfig.trackSecretId,
      secret: sharedConfig.trackSecret,
      aes_key: sharedConfig.trackAesKey,
      devMode: this.options.debug,
    }

    const mergedSettings: TrackSDKSettings = {
      ...DEFAULT_SETTINGS,
      ...config,
    }
    mergedSettings.devMode = initParams.devMode

    this.log('[StarTrackClient] init', initParams)

    this.initingPromise = this.client.init(initParams, { setting: mergedSettings }).then(() => {
      this.inited = true
      this.initStamp = Date.now()
      this.initingPromise = null
    })

    return this.initingPromise
  }

  /**
   * 一站式：加载 SDK 脚本 → 初始化。
   * 适合页面入口调用，等价于 ensureSDK() + init(config)。
   */
  async setup(config?: TrackSDKSettings): Promise<void> {
    await this.ensureSDK()
    await this.init(config)

    this.log('[StarTrackClient] setup completed')
  }

  // —— 公共属性 ——

  /** 设置公共属性 */
  setCommonProperties(props: Record<string, any>) {
    this.commonProperties = { ...this.commonProperties, ...props }
  }

  /** 添加公共属性 */
  addCommonProperties(props: Record<string, any>) {
    this.commonProperties = { ...this.commonProperties, ...props }
  }

  // —— 事件上报 ——

  /** 等待 SDK 加载与初始化完成，返回 client 是否就绪 */
  private async waitUntilReady(eventName: string): Promise<boolean> {
    if (this.sdkPromise) {
      this.log(`[StarTrackClient] event waiting: sdk loading. (${eventName})`)
      await this.sdkPromise
    }

    if (this.initingPromise) {
      this.log(`[StarTrackClient] event waiting: initing. (${eventName})`)
      await this.initingPromise
    }

    if (!this.client) {
      this.log(`[StarTrackClient] event failed: client not initialized. (${eventName})`)
      return false
    }

    return true
  }

  /** 上报埋点事件，自动等待 init 完成、注入公共属性与 device id */
  async event(eventName: string, properties?: Record<string, any>) {
    const ready = await this.waitUntilReady(eventName)
    if (!ready) return

    this.log(`[StarTrackClient] event: ${eventName}`, properties)

    const { st_device_id } = this.client.deviceData ?? {}

    this.client.event(
      {
        st_event_name: eventName,
        st_role_id: '',
        st_distinct_id: st_device_id,
        properties: { ...this.commonProperties, ...properties },
      },
      { addDeviceInfo: true },
    )
  }

  // —— 工具方法 ——

  /** 自 init 完成起的持续秒数，未 init 返回 0 */
  getDurationSecs(): number {
    if (!this.initStamp) return 0
    return Math.round((Date.now() - this.initStamp) / 1000)
  }

  private get isDebug(): boolean {
    return this.options.debug ?? isDebugMode('debug')
  }

  private log(...args: any[]) {
    if (this.isDebug) {
      console.log(...args)
    }
  }
}

// —— 便利工厂 ——

export const createTrackClient = (opts?: TrackClientOptions): StarTrackClient => new StarTrackClient(opts)
